import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorState } from '@ui-kit';
import { FieldDiffView } from './FieldDiffView';
import { useRevisionDiff } from '../hooks/useRevisionDiff';
import type { ApiError } from '../types/changeHistoryViewer';

interface RevisionDiffDrawerProps {
  entityId: number;
  fromRev: number;
  toRev: number;
  onClose: () => void;
}

/**
 * RevisionDiffDrawer — 두 revision 선택 비교 결과 (우측 drawer)
 * dialog 상태 구현. Esc 닫기, 포커스 트랩.
 */
export function RevisionDiffDrawer({ entityId, fromRev, toRev, onClose }: RevisionDiffDrawerProps) {
  const { t } = useTranslation();
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const { data, isLoading, isError, error, refetch } = useRevisionDiff(entityId, fromRev, toRev);

  // 드로어 열릴 때 포커스 이동
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  // Esc 키 닫기
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // 오류 메시지 결정
  const apiErr = error as unknown as ApiError | undefined;
  const errorMsg = (() => {
    if (!apiErr) return t('changeHistoryViewer.error.generic');
    if (apiErr.code === 'AUTH-403-001') return t('changeHistoryViewer.error.forbidden');
    if (apiErr.code === 'HISTORY-404-001') return t('changeHistoryViewer.error.revisionNotFound');
    if (apiErr.code === 'CHGHIST-422-001') return t('changeHistoryViewer.error.invalidRange');
    return apiErr.message ?? t('changeHistoryViewer.error.generic');
  })();

  return (
    <>
      {/* 백드롭 */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 900,
          background: 'rgba(0,0,0,0.35)',
        }}
        onClick={onClose}
      />

      {/* 드로어 패널 */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="diff-drawer-title"
        aria-describedby="diff-drawer-desc"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          width: 'min(600px, 90vw)',
          background: 'var(--color-surface-background)',
          boxShadow: 'var(--shadow-xl)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--spacing-4) var(--spacing-5)',
            borderBottom: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-muted)',
            flexShrink: 0,
          }}
        >
          <div>
            <h2
              id="diff-drawer-title"
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                margin: 0,
              }}
            >
              {t('changeHistoryViewer.diff.title')}
            </h2>
            <p
              id="diff-drawer-desc"
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
                margin: 'var(--spacing-1) 0 0',
              }}
            >
              {t('changeHistoryViewer.diff.from')}{' '}
              <strong>rev {fromRev}</strong>
              {' → '}
              {t('changeHistoryViewer.diff.to')}{' '}
              <strong>rev {toRev}</strong>
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label={t('changeHistoryViewer.common.close')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-sm)',
              background: 'transparent',
              border: '1px solid var(--color-surface-border)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-lg)',
              color: 'var(--color-text-secondary)',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            ×
          </button>
        </div>

        {/* 타임 범위 메타 */}
        {data && (
          <div
            style={{
              padding: 'var(--spacing-3) var(--spacing-5)',
              borderBottom: '1px solid var(--color-surface-border)',
              background: 'var(--color-surface-elevated)',
              display: 'flex',
              gap: 'var(--spacing-6)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-secondary)',
              flexShrink: 0,
              flexWrap: 'wrap',
            }}
          >
            <span>
              <strong>{t('changeHistoryViewer.diff.from')}:</strong>{' '}
              {data.fromRevisionedAt
                ? new Date(data.fromRevisionedAt).toLocaleString('ko-KR')
                : t('changeHistoryViewer.common.unknown')}
            </span>
            <span>
              <strong>{t('changeHistoryViewer.diff.to')}:</strong>{' '}
              {data.toRevisionedAt
                ? new Date(data.toRevisionedAt).toLocaleString('ko-KR')
                : t('changeHistoryViewer.common.unknown')}
            </span>
          </div>
        )}

        {/* 컨텐츠 영역 — 5상태 */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
          }}
        >
          {/* loading */}
          {isLoading && (
            <div
              style={{
                padding: 'var(--spacing-6)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-2)',
              }}
              aria-busy="true"
              aria-label={t('changeHistoryViewer.common.loading')}
            >
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    height: 36,
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    opacity: 1 - i * 0.15,
                  }}
                />
              ))}
            </div>
          )}

          {/* error */}
          {isError && (
            <div style={{ padding: 'var(--spacing-6)' }}>
              <ErrorState
                title={t('changeHistoryViewer.error.generic')}
                message={errorMsg}
                onRetry={() => void refetch()}
              />
            </div>
          )}

          {/* default — diff 결과 */}
          {!isLoading && !isError && data && (
            <FieldDiffView changes={data.changes} />
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </>
  );
}
